// Custom-report generation: the post-dialog half of the C++
// report_menu_item_cls::check_and_perform. Prompts for the output file, runs
// the matching generator — `create_excel_report` (the in-process Excel report
// engine) for .xls/.xlsx templates, `create_xml_report` (the in-process XML
// report engine) for everything else — and offers to open the result.

import { getTigerResource, tigerFormat } from "./tigerResource";
import type { ReportTimeRange } from "./reportDialogState";
import { dateTimeToTigerSeconds } from "./tigerTime";
import { PickerKey, pickerSavePath, rememberPickerDir } from "./pickerFolder";
import { downloadReport } from "./downloadReport";
import { promptReportSave } from "./reportSavePrompt";
import { EXCEL_REPORT_FORMATS, toTauriFilters, xmlReportFormats } from "./reportFormats";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Excel templates are rendered by the in-process Excel report engine;
 *  everything else goes through create_xml_report. */
export function isExcelReport(reportFile: string): boolean {
  return /\.xlsx?$/i.test(reportFile);
}

/** "YYYY-MM-DD" (input value) → "DD/MM/YYYY" (C++ RWDate_to_string). */
function toDdMmYyyy(dateIso: string): string {
  const [y, m, d] = dateIso.split("-");
  return `${d}/${m}/${y}`;
}

/** "HH:MM" / "HH:MM:SS" → "HH:MM:SS". */
function toHhMmSs(time: string): string {
  return time.length === 5 ? `${time}:00` : time;
}

/** Start/end window as the generator's params string
 *  (C++ `RWTime_to_string(start) + " " + RWTime_to_string(end)`). */
export function excelParamsForRange(range: ReportTimeRange): string {
  return (
    `${toHhMmSs(range.startTime)} ${toDdMmYyyy(range.startDate)} ` +
    `${toHhMmSs(range.endTime)} ${toDdMmYyyy(range.endDate)}`
  );
}

/** MONTH report params (C++ `RWDate_to_string(g_report_month_date)`, day 1). */
export function excelParamsForMonth(month: number, year: number): string {
  return `01/${String(month).padStart(2, "0")}/${String(year).padStart(4, "0")}`;
}

/** YEAR report params (day 1, January). */
export function excelParamsForYear(year: number): string {
  return excelParamsForMonth(1, year);
}

/** The window a report covers, in both generators' shapes: the Excel params
 *  string and the raw range (+ DATE_STATE turbine state) for the XML engine. */
export interface ReportWindow {
  excelParams: string;
  range: ReportTimeRange;
  turbineState?: string;
}

export function reportWindowForRange(range: ReportTimeRange, turbineState?: string): ReportWindow {
  return { excelParams: excelParamsForRange(range), range, turbineState };
}

/** MONTH reports cover the whole month (the Excel generator gets day 1). */
export function reportWindowForMonth(month: number, year: number): ReportWindow {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, "0");
  return {
    excelParams: excelParamsForMonth(month, year),
    range: {
      startDate: `${year}-${mm}-01`,
      startTime: "00:00:00",
      endDate: `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
      endTime: "23:59:59",
    },
  };
}

/** YEAR reports cover the whole year. */
export function reportWindowForYear(year: number): ReportWindow {
  return {
    excelParams: excelParamsForYear(year),
    range: {
      startDate: `${year}-01-01`,
      startTime: "00:00:00",
      endDate: `${year}-12-31`,
      endTime: "23:59:59",
    },
  };
}

export interface CustomReportContext {
  /** Config the report runs against (passed to the generator). */
  configName: string;
  /** The clicked turbine's config — names the default output file (C++
   *  `my_config_name + "_tiger_report"`). */
  menuConfigName: string;
  /** The report's ini name (`report[N]`). */
  reportName: string;
  /** Template path relative to the Tiger root. */
  reportFile: string;
}

/** Prompt for the output file, run the Excel generator, then offer to open the
 *  result. Desktop (Tauri) only — the save/ask dialogs need a native shell.
 *  `onBusy` brackets the generator run (the busy dialog; C++
 *  Ui_progress_info_dialog_start). */
async function generateExcelReport(
  ctx: CustomReportContext,
  params: string,
  onBusy?: (busy: boolean) => void,
): Promise<void> {
  if (!isTauri()) {
    // Excel templates write one format only, so the dialog is really just the
    // "where/what name" half the desktop gets from its native save dialog.
    const filename = await promptReportSave({
      defaultName: `${ctx.menuConfigName}_tiger_report.xlsx`,
      formats: EXCEL_REPORT_FORMATS,
      rememberKey: PickerKey.excelReport,
    });
    if (!filename) return; // cancelled
    const search = new URLSearchParams({
      config_name: ctx.configName,
      report_id: ctx.reportName,
      filename,
      params,
    });
    onBusy?.(true);
    try {
      await downloadReport(`/api/create_excel_report?${search}`, filename);
    } catch (e) {
      globalThis.alert(e instanceof Error ? e.message : String(e));
    } finally {
      onBusy?.(false);
    }
    return;
  }
  const { save, ask, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  const outputFile = await save({
    title: getTigerResource("TIGER_SAVE_REPORT_TITLE"),
    defaultPath: await pickerSavePath(PickerKey.excelReport, `${ctx.menuConfigName}_tiger_report.xlsx`),
    filters: toTauriFilters(EXCEL_REPORT_FORMATS),
  });
  if (!outputFile) return; // cancelled
  rememberPickerDir(PickerKey.excelReport, outputFile);

  const title = getTigerResource("TIGER_REPORT_SUCCESS_TITLE");
  onBusy?.(true);
  try {
    await invoke("create_excel_report", {
      configName: ctx.configName,
      reportId: ctx.reportName,
      outputFile,
      params,
    });
  } catch (e) {
    onBusy?.(false);
    await message(String(e), { title, kind: "error" });
    return;
  }
  onBusy?.(false);

  const openIt = await ask(
    tigerFormat(getTigerResource("TIGER_REPORT_SUCCESS_MSG"), outputFile),
    { title, kind: "info" },
  );
  if (openIt) {
    await invoke("open_report_file", { path: outputFile }).catch((e) =>
      console.error("open_report_file failed:", e),
    );
  }
}

/** Prompt for the output file and run the XML report engine
 *  (`create_xml_report`). Word XML templates write .doc/.xml directly; .fodt
 *  templates can also convert to .docx/.pdf through `convert_file`
 *  (tiger_format_convert_rs or the convert tunnel). */
async function generateXmlReport(
  ctx: CustomReportContext,
  window: ReportWindow,
  onBusy?: (busy: boolean) => void,
): Promise<void> {
  if (!isTauri()) {
    // NB: `window` here is the ReportWindow param, not the global — DOM access
    // stays inside downloadReport / promptReportSave / globalThis.
    const isFodt = /\.fodt$/i.test(ctx.reportFile);
    // The same format lists the desktop dialog offers: a .fodt template can also
    // convert to .docx / .pdf, which run_xml_report does server-side here too.
    const filename = await promptReportSave({
      defaultName: `${ctx.menuConfigName}_tiger_report.${isFodt ? "odt" : "doc"}`,
      formats: xmlReportFormats(ctx.reportFile),
      rememberKey: PickerKey.xmlReport,
    });
    if (!filename) return; // cancelled
    const startTime = dateTimeToTigerSeconds(window.range.startDate, window.range.startTime);
    const endTime = dateTimeToTigerSeconds(window.range.endDate, window.range.endTime);
    const search = new URLSearchParams({
      config_name: ctx.menuConfigName,
      report_name: ctx.reportName,
      filename,
      start_time: String(startTime ?? 0),
      end_time: String(endTime ?? 0),
      turbine_state: window.turbineState ?? "",
    });
    onBusy?.(true);
    try {
      // The server renders graphs/diagnostics via the headless worker and streams
      // the finished report back; this tab renders nothing.
      const { warning } = await downloadReport(`/api/create_xml_report?${search}`, filename);
      // A .pdf/.docx the server could not convert arrives in the format it was
      // generated in; say so rather than leave the changed extension unexplained.
      if (warning) globalThis.alert(warning);
    } catch (e) {
      globalThis.alert(e instanceof Error ? e.message : String(e));
    } finally {
      onBusy?.(false);
    }
    return;
  }
  const { save, ask, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  const isFodt = /\.fodt$/i.test(ctx.reportFile);
  const outputFile = await save({
    title: getTigerResource("TIGER_SAVE_REPORT_TITLE"),
    defaultPath: await pickerSavePath(PickerKey.xmlReport, `${ctx.menuConfigName}_tiger_report.${isFodt ? "odt" : "doc"}`),
    // The C++ CFileDialog filters from report_menu_item_cls::check_and_perform.
    filters: toTauriFilters(xmlReportFormats(ctx.reportFile)),
  });
  if (!outputFile) return; // cancelled
  rememberPickerDir(PickerKey.xmlReport, outputFile);

  const startTime = dateTimeToTigerSeconds(window.range.startDate, window.range.startTime);
  const endTime = dateTimeToTigerSeconds(window.range.endDate, window.range.endTime);

  const title = getTigerResource("TIGER_REPORT_SUCCESS_TITLE");
  onBusy?.(true);
  try {
    await invoke("create_xml_report", {
      // The report ini (and report_id) belong to the clicked turbine's config.
      configName: ctx.menuConfigName,
      reportName: ctx.reportName,
      outputFile,
      startTime: startTime ?? 0,
      endTime: endTime ?? 0,
      turbineState: window.turbineState ?? "",
    });
  } catch (e) {
    onBusy?.(false);
    await message(String(e), { title, kind: "error" });
    return;
  }
  onBusy?.(false);

  const openIt = await ask(
    tigerFormat(getTigerResource("TIGER_REPORT_SUCCESS_MSG"), outputFile),
    { title, kind: "info" },
  );
  if (openIt) {
    await invoke("open_report_file", { path: outputFile }).catch((e) =>
      console.error("open_report_file failed:", e),
    );
  }
}

/** Route a custom report's collected window to its generator. */
export async function generateCustomReport(
  ctx: CustomReportContext,
  window: ReportWindow,
  onBusy?: (busy: boolean) => void,
): Promise<void> {
  if (isExcelReport(ctx.reportFile)) {
    await generateExcelReport(ctx, window.excelParams, onBusy);
  } else {
    await generateXmlReport(ctx, window, onBusy);
  }
}
